import { Home, Car, Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export const ServicesSection = ({ onGetQuote }) => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-16 md:py-24 bg-background relative noise-bg"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4 opacity-0 animate-fade-in-up">
            Insurance That Fits Your Life
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto opacity-0 animate-fade-in-up animation-delay-100">
            Whether you're protecting your home, car, or family's future, we've got you covered with personalized policies and friendly service.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Home Insurance Card */}
          <Card
            data-testid="service-card-home"
            className="service-card group overflow-hidden bg-white border border-border rounded-2xl opacity-0 animate-fade-in-up animation-delay-200"
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src="https://images.pexels.com/photos/277667/pexels-photo-277667.jpeg"
                alt="Home Insurance"
                className="w-full h-full object-cover img-zoom"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <div className="h-10 w-10 rounded-full bg-white/90 flex items-center justify-center">
                  <Home className="h-5 w-5 text-primary" />
                </div>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Home Insurance
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Protect your most valuable asset. Coverage for your home, belongings, and liability.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Dwelling coverage
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Personal property
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Liability protection
                </li>
              </ul>
              <Button
                data-testid="service-quote-btn-home"
                onClick={onGetQuote}
                variant="outline"
                className="w-full rounded-full border-primary text-primary hover:bg-primary hover:text-white group-hover:bg-primary group-hover:text-white transition-all duration-300"
              >
                Get a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>

          {/* Auto Insurance Card */}
          <Card
            data-testid="service-card-auto"
            className="service-card group overflow-hidden bg-white border border-border rounded-2xl opacity-0 animate-fade-in-up animation-delay-300"
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1567788807974-2aa041089c56"
                alt="Auto Insurance"
                className="w-full h-full object-cover img-zoom"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <div className="h-10 w-10 rounded-full bg-white/90 flex items-center justify-center">
                  <Car className="h-5 w-5 text-primary" />
                </div>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Auto Insurance
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Drive with confidence. Comprehensive coverage for your vehicles at competitive rates.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Collision coverage
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Comprehensive
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Roadside assistance
                </li>
              </ul>
              <Button
                data-testid="service-quote-btn-auto"
                onClick={onGetQuote}
                variant="outline"
                className="w-full rounded-full border-primary text-primary hover:bg-primary hover:text-white group-hover:bg-primary group-hover:text-white transition-all duration-300"
              >
                Get a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>

          {/* Life Insurance Card */}
          <Card
            data-testid="service-card-life"
            className="service-card group overflow-hidden bg-white border border-border rounded-2xl opacity-0 animate-fade-in-up animation-delay-400"
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1687456338392-fc20bca1b4b5"
                alt="Life Insurance"
                className="w-full h-full object-cover img-zoom"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <div className="h-10 w-10 rounded-full bg-white/90 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Life Insurance
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Secure your family's future. Financial protection for the ones you love most.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Term life
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Whole life
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  Flexible premiums
                </li>
              </ul>
              <Button
                data-testid="service-quote-btn-life"
                onClick={onGetQuote}
                variant="outline"
                className="w-full rounded-full border-primary text-primary hover:bg-primary hover:text-white group-hover:bg-primary group-hover:text-white transition-all duration-300"
              >
                Get a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
